import type { IFeedbackFormValues, IFeedbackProps } from '../types';

const DEFAULT_EMAIL = 'helpcentercsat@klaviyo.com';

export const INITIAL_VALUES: IFeedbackFormValues = {
  improvements: '',
  allowContact: false,
  email: '',
  reason: '',
};

export const PUBLIC_GRAPHQL_ENDPOINT = 'https://k-ops.services.klaviyo.com/helpcenter';

export const EXCLUDED_ARTICLES = [
  '115005078867',
  '115001002272',
  '360050759151',
  '1260805595309',
  '115000976672',
  '115005251028',
];

// see https://developers.klaviyo.com/en/reference/create_client_event
const createClientEvent = async ({ email, name, properties }: { email: string; name: string; properties: object; }) => {
  try {
    const companyId = await window.klaviyo.account() || '9BX3wh';
    const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'revision': '2024-07-15',
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties,
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name,
                },
              },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                },
              },
            },
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Klaviyo event creation failed with error: ', error);
    return false;
  }
};

const getSiteTitle = (type: IFeedbackProps['type']) => {
  switch (type) {
    case 'devPortal':
      return 'Developer Portal';
    case 'hc':
      return 'HC';
    case 'marketing':
      return 'Marketing Site';
    default:
      return 'Unknown Site';
  }
};

export const submitFeedback = async ({
  category,
  locale,
  type,
  values,
  vote,
}: IFeedbackProps) => {
  try {
    const isIdentified = await window.klaviyo.isIdentified();
    const siteTitle = getSiteTitle(type);
    const siteCode = siteTitle.replaceAll(' ', '');
    const event = `${siteTitle} Feedback Submitted`;

    const submission = {
      path: window.location.href,
      category: category,
      [`${siteCode}ArticleFeedback`]: values?.improvements,
      [`${siteCode}ArticleIssue`]: values?.reason,
      [`${siteCode}ArticleSentiment`]: vote === 1 ? 'Positive' : 'Negative',
      allowContact: values?.allowContact,
      locale: locale,
    };

    if (isIdentified) {
      return await window.klaviyo.track(event, submission);
    }
    
    if (values?.email) {
      return await window.klaviyo.identify({ '$email' : values.email }, () => {
        window.klaviyo.track(event, submission);
      });
    }

    // we have a placeholder email so we need to create a client event
    // to prevent the __kla_id cookie from persisting with the placeholder email
    // and attributing future events to the placeholder email
    return await createClientEvent({
      email: DEFAULT_EMAIL,
      name: event,
      properties: submission,
    });

  } catch (error) {
    console.error('Feedback submission failed with error: ', error);
    return false;
  }
};
